import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  App as AntdApp,
  Button,
  Card,
  Descriptions,
  Flex,
  Form,
  Popover,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography
} from 'antd'
import { Info, Pencil, Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import {
  organizationsAccessListInfiniteOptions,
  organizationsAccessPartialUpdateMutation,
  organizationsAccessRetrieveOptions,
  organizationsTeamsListInfiniteOptions,
  organizationsUserGroupsPartialUpdateMutation,
  OrganizationRole
} from 'src/api/main'
import type {
  OrganizationsAccessListData,
  OrganizationsTeamsListData,
  PaginatedOrganizationUsersList,
  PaginatedTeamListList,
  TeamList,
  UserGroup
} from 'src/api/main'
import { DateTimeCell, InfiniteSelect, UserCell } from 'src/components'
import { useTeamMemberMutations } from 'src/features/organization/hooks'
import { useHasWorkspacePermissions } from 'src/hooks'
import { paths } from 'src/router'
import {
  useShowApiErrors,
  WorkspacePermissionCode,
  type OptionTransformType
} from 'src/utils'

import {
  invalidateWorkspaceUserGroups,
  useWorkspaceUserGroups,
  workspaceUsersListQueryKey
} from '../hooks'
import type { WorkspaceUser } from '../types'
import { composeDisplayName } from '../utils'

import { MemberRoleTag } from './MemberRoleTag'
import { UserRoleAssignForm } from './UserRoleAssignForm'

const { Text } = Typography

// Hoisted so the reference stays stable across renders. 500 matches the app-wide
// "fetch all" page size used by the org chart / teams hooks.
const ALL_GROUPS_QUERY = { per_page: 500 } as const

type Props = {
  record: WorkspaceUser
  workspaceId: number
  /** Gate the role / add-to-team popovers (e.g. own-profile view can't edit). */
  editable?: boolean
}

/** Inline team picker rendered inside the "add to team" popover. */
const AddTeamPopoverForm = ({
  record,
  workspaceId,
  onDone
}: {
  record: WorkspaceUser
  workspaceId: number
  onDone: () => void
}) => {
  const { t } = useTranslation()
  const [form] = Form.useForm<{ team: number }>()
  const { addMember } = useTeamMemberMutations(workspaceId)

  // Membership lives on leaf teams only; exclude teams the user already joined.
  // The backend has no "team" (leaf) type filter — it's derived client-side via
  // `apiTypeToKind` — so the filtering stays in `getOptions` per page.
  const joinedTeamIds = new Set((record.teams ?? []).map((tm) => tm.id))

  return (
    <Flex vertical gap={8} style={{ width: 240 }}>
      <Form
        form={form}
        layout="vertical"
        onFinish={(values) =>
          addMember.mutate(
            { body: { user: record.user.id, team: values.team } },
            { onSuccess: onDone }
          )
        }
      >
        <Form.Item
          name="team"
          label={t('users.addToTeam.teamLabel')}
          rules={[
            { required: true, message: t('users.addToTeam.teamRequired') }
          ]}
          style={{ marginBottom: 0 }}
        >
          <InfiniteSelect<
            OrganizationsTeamsListData,
            PaginatedTeamListList,
            TeamList
          >
            autoFocus
            defaultOpen
            allowClear={false}
            getQueryFunction={organizationsTeamsListInfiniteOptions}
            // Leaf teams (API type `section`) filtered server-side; the
            // already-joined exclusion stays client-side (not a server filter).
            baseOptions={{
              query: { organization: workspaceId, type: ['section'] }
            }}
            getOptions={({ options }: OptionTransformType<TeamList>) =>
              options
                .filter((u) => !joinedTeamIds.has(u.id))
                .map((u) => ({ value: u.id, label: u.name }))
            }
            placeholder={t('users.addToTeam.teamPlaceholder')}
          />
        </Form.Item>
      </Form>
      <Flex justify="flex-end" gap={8}>
        <Button size="small" onClick={onDone} disabled={addMember.isPending}>
          {t('users.addToTeam.cancel')}
        </Button>
        <Button
          size="small"
          type="primary"
          loading={addMember.isPending}
          onClick={() => form.submit()}
        >
          {t('users.addToTeam.submit')}
        </Button>
      </Flex>
    </Flex>
  )
}

/** Inline group picker rendered inside the "add to group" popover. */
const AddGroupPopoverForm = ({
  record,
  workspaceId,
  groups,
  onDone
}: {
  record: WorkspaceUser
  workspaceId: number
  /** Every group in the workspace — the caller already fetched them. */
  groups: UserGroup[]
  onDone: () => void
}) => {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const qc = useQueryClient()
  const showApiErrors = useShowApiErrors()
  const [form] = Form.useForm<{ group: number }>()

  const { mutate, isPending } = useMutation({
    ...organizationsUserGroupsPartialUpdateMutation(),
    onSuccess: async () => {
      await invalidateWorkspaceUserGroups(qc, workspaceId)
      message.success(t('users.addToGroup.success'))
      onDone()
    },
    onError: (e) => showApiErrors(e, form)
  })

  const options = groups
    .filter((g) => !g.users.some((u) => u.id === record.user.id))
    .map((g) => ({ value: g.id, label: g.name }))

  // The endpoint takes the whole membership list, so the current members are
  // resent alongside the new one.
  const submit = ({ group: groupId }: { group: number }) => {
    const group = groups.find((g) => g.id === groupId)
    if (!group) return
    mutate({
      path: { id: groupId },
      body: { users: [...group.users.map((u) => u.id), record.user.id] }
    })
  }

  return (
    <Flex vertical gap={8} style={{ width: 240 }}>
      <Form form={form} layout="vertical" onFinish={submit}>
        <Form.Item
          name="group"
          label={t('users.addToGroup.groupLabel')}
          rules={[
            { required: true, message: t('users.addToGroup.groupRequired') }
          ]}
          style={{ marginBottom: 0 }}
        >
          <Select
            autoFocus
            defaultOpen
            showSearch
            optionFilterProp="label"
            options={options}
            placeholder={t('users.addToGroup.groupPlaceholder')}
            notFoundContent={t('users.addToGroup.noGroups')}
          />
        </Form.Item>
      </Form>
      <Flex justify="flex-end" gap={8}>
        <Button size="small" onClick={onDone} disabled={isPending}>
          {t('users.addToGroup.cancel')}
        </Button>
        <Button
          size="small"
          type="primary"
          loading={isPending}
          onClick={() => form.submit()}
        >
          {t('users.addToGroup.submit')}
        </Button>
      </Flex>
    </Flex>
  )
}

/** Inline manager picker rendered inside the "reports to" popover. */
const HeadUserPopoverForm = ({
  record,
  workspaceId,
  onDone
}: {
  record: WorkspaceUser
  workspaceId: number
  onDone: () => void
}) => {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const qc = useQueryClient()
  const showApiErrors = useShowApiErrors()
  const [form] = Form.useForm<{ head_user: number | null }>()

  const { mutate, isPending } = useMutation({
    ...organizationsAccessPartialUpdateMutation(),
    onSuccess: () => {
      message.success(t('users.detail.workspaceAccess.headUserUpdated'))
      void qc.invalidateQueries({
        queryKey: workspaceUsersListQueryKey(workspaceId),
        refetchType: 'all'
      })
      onDone()
    },
    onError: (e) => showApiErrors(e, form)
  })

  return (
    <Flex vertical gap={8} style={{ width: 260 }}>
      <Form
        form={form}
        layout="vertical"
        initialValues={{ head_user: record.head_user?.id ?? null }}
        onFinish={(values) =>
          mutate({
            path: { id: record.id },
            body: { head_user: values.head_user ?? null }
          })
        }
      >
        <Form.Item
          name="head_user"
          label={t('users.detail.workspaceAccess.headUser')}
          style={{ marginBottom: 0 }}
        >
          <InfiniteSelect<
            OrganizationsAccessListData,
            PaginatedOrganizationUsersList,
            WorkspaceUser
          >
            autoFocus
            defaultOpen
            allowClear
            getQueryFunction={organizationsAccessListInfiniteOptions}
            baseOptions={{
              query: {
                organization: workspaceId,
                // Pin the current manager to the top so its option loads on the
                // first page and the label renders. `preference` keys off the
                // access-record id, not the user id that the value carries.
                preference: record.head_user?.organization_access_id
                  ? String(record.head_user.organization_access_id)
                  : undefined
              }
            }}
            getOptions={({ options }: OptionTransformType<WorkspaceUser>) =>
              // A member can't report to themselves.
              options
                .filter((u) => u.user.id !== record.user.id)
                .map((u) => ({
                  value: u.user.id,
                  label: `${composeDisplayName(
                    u.user.first_name,
                    u.user.last_name,
                    u.user.email
                  )} (${u.user.email})`
                }))
            }
            placeholder={t('users.detail.workspaceAccess.headUserPlaceholder')}
          />
        </Form.Item>
      </Form>
      <Flex justify="flex-end" gap={8}>
        <Button size="small" onClick={onDone} disabled={isPending}>
          {t('users.modals.changeRole.cancel')}
        </Button>
        <Button
          size="small"
          type="primary"
          loading={isPending}
          onClick={() => form.submit()}
        >
          {t('users.modals.changeRole.submit')}
        </Button>
      </Flex>
    </Flex>
  )
}

export const UserWorkspaceAccessCard = ({
  record,
  workspaceId,
  editable = true
}: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [teamOpen, setTeamOpen] = useState(false)
  const [groupOpen, setGroupOpen] = useState(false)
  const [headUserOpen, setHeadUserOpen] = useState(false)
  const [roleOpen, setRoleOpen] = useState(false)

  // Assigning roles is its own endpoint with its own code: `POST
  // access/{id}/roles/` answers to `roles.manage`, not to `users.manage`.
  const [mayManageRoles, mayViewGroups, mayManageGroups] =
    useHasWorkspacePermissions([
      WorkspacePermissionCode.ROLES_MANAGE,
      WorkspacePermissionCode.GROUPS_VIEW,
      WorkspacePermissionCode.GROUPS_MANAGE
    ])
  const canChangeRole = mayManageRoles && record.role !== OrganizationRole.OWNER

  // `/organizations/user-groups/` has no `user` filter, so membership is derived
  // from the full workspace list. Groups are few compared to members, and the
  // add-to-group picker needs the same list anyway.
  const { groups } = useWorkspaceUserGroups({
    workspaceId,
    query: ALL_GROUPS_QUERY,
    enabled: mayViewGroups
  })
  const memberGroups = useMemo(
    () => groups.filter((g) => g.users.some((u) => u.id === record.user.id)),
    [groups, record.user.id]
  )

  // `head_user` carries no avatar; fetch the manager's access record whose
  // `user.photo` embeds the attachment (same source as the hero avatar).
  const headAccessId = record.head_user?.organization_access_id
  const { data: headAccess } = useQuery({
    ...organizationsAccessRetrieveOptions({
      path: { id: Number(headAccessId) }
    }),
    enabled: !!headAccessId
  })
  const headPhotoUrl = headAccess?.user.photo?.file

  return (
    <Card
      size="small"
      title={
        <Flex align="center" gap={6}>
          <Text strong style={{ fontSize: 13 }}>
            {t('users.detail.workspaceAccess.title')}
          </Text>
          <Tooltip title={t('users.detail.workspaceAccess.subtitle')}>
            <Info
              size={12}
              style={{
                color: 'var(--ant-color-text-tertiary)',
                cursor: 'help'
              }}
              aria-label={t('users.detail.workspaceAccess.subtitle')}
            />
          </Tooltip>
        </Flex>
      }
    >
      <Descriptions
        column={3}
        size="small"
        colon={false}
        styles={{
          label: { alignItems: 'center' },
          content: { alignItems: 'center', fontSize: 12 }
        }}
      >
        <Descriptions.Item label={t('users.detail.workspaceAccess.role')}>
          <Flex align="center" gap={8} wrap style={{ minWidth: 0 }}>
            <MemberRoleTag record={record} />
            {canChangeRole ? (
              <Popover
                open={roleOpen}
                onOpenChange={setRoleOpen}
                trigger="click"
                placement="bottomLeft"
                destroyOnHidden
                title={t('users.modals.changeRole.title')}
                content={
                  <div style={{ width: 260 }}>
                    <UserRoleAssignForm
                      record={record}
                      workspaceId={workspaceId}
                      onDone={() => setRoleOpen(false)}
                    />
                  </div>
                }
              >
                <Button
                  type="text"
                  size="small"
                  icon={<Pencil size={13} />}
                  aria-label={t('users.actions.changeRole')}
                />
              </Popover>
            ) : null}
          </Flex>
        </Descriptions.Item>
        <Descriptions.Item label={t('users.detail.workspaceAccess.joined')}>
          <DateTimeCell value={record.joined_date} variant="date" />
        </Descriptions.Item>
        <Descriptions.Item label={t('users.detail.workspaceAccess.headUser')}>
          <Flex align="center" gap={8} style={{ minWidth: 0 }}>
            {record.head_user ? (
              <div style={{ minWidth: 0, flex: 1 }}>
                <UserCell
                  firstName={record.head_user.first_name}
                  lastName={record.head_user.last_name}
                  email={record.head_user.email}
                  photoUrl={headPhotoUrl}
                  size="sm"
                  to={paths.workspaces.membersPerson(
                    String(workspaceId),
                    String(record.head_user.id)
                  )}
                  copyableMeta
                />
              </div>
            ) : (
              <Text type="secondary">—</Text>
            )}
            {editable ? (
              <Popover
                open={headUserOpen}
                onOpenChange={setHeadUserOpen}
                trigger="click"
                placement="bottomLeft"
                destroyOnHidden
                title={t('users.detail.workspaceAccess.headUser')}
                content={
                  <HeadUserPopoverForm
                    record={record}
                    workspaceId={workspaceId}
                    onDone={() => setHeadUserOpen(false)}
                  />
                }
              >
                <Button
                  type="text"
                  size="small"
                  icon={<Pencil size={13} />}
                  aria-label={t('users.detail.workspaceAccess.headUser')}
                />
              </Popover>
            ) : null}
          </Flex>
        </Descriptions.Item>
        <Descriptions.Item
          label={t('users.detail.workspaceAccess.teams')}
          span={3}
        >
          <Flex align="center" gap={8} wrap>
            {record.teams && record.teams.length > 0 ? (
              <Space size={[4, 4]} wrap>
                {record.teams.map((team) => (
                  <Tag
                    key={team.id}
                    style={{ cursor: 'pointer', marginInlineEnd: 0 }}
                    onClick={() =>
                      navigate(
                        paths.workspaces.membersTeam(
                          String(workspaceId),
                          String(team.id)
                        )
                      )
                    }
                  >
                    {team.name}
                  </Tag>
                ))}
              </Space>
            ) : (
              <Text type="secondary">
                {t('users.detail.workspaceAccess.noTeams')}
              </Text>
            )}
            {editable ? (
              <Popover
                open={teamOpen}
                onOpenChange={setTeamOpen}
                trigger="click"
                placement="bottomLeft"
                destroyOnHidden
                title={t('users.addToTeam.title')}
                content={
                  <AddTeamPopoverForm
                    record={record}
                    workspaceId={workspaceId}
                    onDone={() => setTeamOpen(false)}
                  />
                }
              >
                <Button
                  type="link"
                  size="small"
                  icon={<Plus size={13} />}
                  style={{ padding: 0, height: 'auto' }}
                >
                  {t('users.addToTeam.action')}
                </Button>
              </Popover>
            ) : null}
          </Flex>
        </Descriptions.Item>
        {mayViewGroups ? (
          <Descriptions.Item
            label={t('users.detail.workspaceAccess.groups')}
            span={3}
          >
            <Flex align="center" gap={8} wrap>
              {memberGroups.length > 0 ? (
                <Space size={[4, 4]} wrap>
                  {memberGroups.map((group) => (
                    <Tag
                      key={group.id}
                      style={{ cursor: 'pointer', marginInlineEnd: 0 }}
                      // Groups have no detail page — jump to the Groups tab with
                      // the name pre-searched instead.
                      onClick={() =>
                        navigate(
                          `${paths.workspaces.membersGroups(
                            String(workspaceId)
                          )}?search=${encodeURIComponent(group.name)}`
                        )
                      }
                    >
                      {group.name}
                    </Tag>
                  ))}
                </Space>
              ) : (
                <Text type="secondary">
                  {t('users.detail.workspaceAccess.noGroups')}
                </Text>
              )}
              {mayManageGroups ? (
                <Popover
                  open={groupOpen}
                  onOpenChange={setGroupOpen}
                  trigger="click"
                  placement="bottomLeft"
                  destroyOnHidden
                  title={t('users.addToGroup.title')}
                  content={
                    <AddGroupPopoverForm
                      record={record}
                      workspaceId={workspaceId}
                      groups={groups}
                      onDone={() => setGroupOpen(false)}
                    />
                  }
                >
                  <Button
                    type="link"
                    size="small"
                    icon={<Plus size={13} />}
                    style={{ padding: 0, height: 'auto' }}
                  >
                    {t('users.addToGroup.action')}
                  </Button>
                </Popover>
              ) : null}
            </Flex>
          </Descriptions.Item>
        ) : null}
      </Descriptions>
    </Card>
  )
}
